import React, { useState, useEffect } from 'react';
import { DollarSign, User, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { consultantsAPI } from '@/api/api';
import api from '@/api/api';
import { generateConsultantReceipt } from '@/utils/consultantReceiptGenerator';
import PaymentBreakdown from '@/components/PaymentBreakdown';
import { formatDate } from '../../utils/dateTimeUtils';

const PayConsultant = () => {
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchConsultants();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const response = await api.get(`/tenants/my-college/settings`);
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await consultantsAPI.getConsultants();
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const handleConsultantChange = async (consultantId) => {
    setSelectedConsultant(consultantId);
    setSelectedStudents([]);
    
    if (!consultantId) {
      setStudents([]);
      return;
    }

    setLoading(true);
    try {
      const response = await consultantsAPI.getStudentWisePayoutReport(consultantId);
      // Filter only pending payouts
      const pendingStudents = response.data.students.filter(s => s.payout_status === 'Pending' && s.payout_amount > 0);
      setStudents(pendingStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to fetch student list. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.student_id));
    }
  };

  const calculateTotalPayout = () => {
    return students
      .filter(s => selectedStudents.includes(s.student_id))
      .reduce((sum, s) => sum + s.payout_amount, 0);
  };

  const handlePayment = async () => {
    if (selectedStudents.length === 0) {
      alert('Please select at least one student to pay commission');
      return;
    }

    if (paymentModes.length === 0) {
      alert('Please add at least one payment mode');
      return;
    }

    const totalPaid = paymentModes.reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
    const totalPayout = calculateTotalPayout();
    
    if (Math.abs(totalPaid - totalPayout) > 0.01) {
      alert(`Payment breakdown total (Rs. ${totalPaid.toLocaleString('en-IN')}) must equal payout amount (Rs. ${totalPayout.toLocaleString('en-IN')})`);
      return;
    }

    if (!window.confirm(`Pay Rs. ${totalPayout.toLocaleString('en-IN')} for ${selectedStudents.length} student(s)?`)) {
      return;
    }

    setProcessing(true);
    try {
      const response = await consultantsAPI.payConsultantStudentWise({
        consultant_id: selectedConsultant,
        student_ids: selectedStudents,
        payment_date: paymentDate,
        payment_modes: paymentModes
      });

      if (response.data.success) {
        setPaymentResult(response.data);
        setShowSuccessModal(true);
        // Refresh the list
        handleConsultantChange(selectedConsultant);
        setSelectedStudents([]);
        setPaymentModes([]);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Failed to process payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handlePrintReceipt = async () => {
    if (paymentResult) {
      const consultant = consultants.find(c => c.id === selectedConsultant);
      const receiptData = {
        receipt_no: paymentResult.receipt_no,
        consultant_name: consultant?.name,
        consultant_email: consultant?.email,
        amount: paymentResult.total_amount,
        payment_date: paymentResult.payment_date,
        payment_mode: 'Student-wise Commission',
        reference_number: `${paymentResult.students_paid} students`,
        created_by_name: paymentResult.created_by_name,
        created_by_role: paymentResult.created_by_role,
        created_by_email: paymentResult.created_by_email
      };
      await generateConsultantReceipt(receiptData, collegeInfo);
    }
  };

  const selectedConsultantData = consultants.find(c => c.id === selectedConsultant);

  return (
    <div className="space-y-6">
      {/* Success Modal */}
      {showSuccessModal && paymentResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-1">Receipt No: <span className="font-semibold">{paymentResult.receipt_no}</span></p>
              <p className="text-gray-600 mb-4">Amount: <span className="font-semibold text-green-600">Rs. {paymentResult.total_amount?.toLocaleString('en-IN')}</span></p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                <p className="text-sm text-gray-600">Consultant: <span className="font-medium text-gray-900">{selectedConsultantData?.name}</span></p>
                <p className="text-sm text-gray-600">Students Paid: <span className="font-medium text-gray-900">{paymentResult.students_paid}</span></p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setPaymentResult(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handlePrintReceipt();
                    setShowSuccessModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  Print Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pay Consultant - Student Wise</h1>
          <p className="text-sm text-gray-500 mt-1">Select students to pay commission</p>
        </div>
      </div>

      {/* Consultant Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex gap-6 items-end flex-wrap">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Consultant
            </label>
            <select
              value={selectedConsultant}
              onChange={(e) => handleConsultantChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Select Consultant --</option>
              {consultants.map((consultant) => (
                <option key={consultant.id} value={consultant.id}>
                  {consultant.name} ({consultant.email})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Date
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="consultant-payment-date"
            />
          </div>
        </div>
      </div>

      {/* Student List */}
      {selectedConsultant && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Pending Commissions</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {students.length} student(s) with pending payout
                </p>
              </div>
              {students.length > 0 && (
                <div className="flex items-center gap-4">
                  <Button
                    onClick={selectAllStudents}
                    variant="outline"
                    size="sm"
                  >
                    {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  {selectedStudents.length > 0 && (
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Selected: {selectedStudents.length}</p>
                      <p className="text-lg font-bold text-green-600">Rs. {calculateTotalPayout().toLocaleString('en-IN')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading students...</p>
            </div>
          ) : students.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <User size={48} className="mx-auto text-gray-300 mb-4" />
              <p>No pending commissions for this consultant</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedStudents.length === students.length}
                        onChange={selectAllStudents}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payout Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {students.map((student) => (
                    <tr 
                      key={student.student_id} 
                      className={`hover:bg-gray-50 ${
                        selectedStudents.includes(student.student_id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.student_id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleStudentSelection(student.student_id);
                          }}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{student.student_name}</div>
                        <div className="text-sm text-gray-500">{student.student_number}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{student.course_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(student.admission_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{student.student_mobile}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                        Rs. {student.payout_amount.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {students.length > 0 && selectedStudents.length > 0 && (
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm text-gray-600">Total Selected Students:</p>
                    <p className="font-semibold">{selectedStudents.length}</p>
                  </div>
                  <div className="flex justify-between items-center mb-4 pb-4 border-b">
                    <p className="text-sm text-gray-600">Total Payout Amount:</p>
                    <p className="text-xl font-bold text-gray-900">Rs. {calculateTotalPayout().toLocaleString('en-IN')}</p>
                  </div>
                  
                  <PaymentBreakdown
                    totalAmount={calculateTotalPayout()}
                    paymentModes={paymentModes}
                    onChange={setPaymentModes}
                    label="Payment Mode Breakdown"
                  />
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button
                  onClick={handlePayment}
                  disabled={processing || paymentModes.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <DollarSign className="mr-2 h-4 w-4" />
                  {processing ? 'Processing...' : 'Pay Commission'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PayConsultant;
